import { useEffect, useRef, useState } from 'react';
import { Home, Ruler, Satellite } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { roofingPitch } from '../lib/format';

type Summary = {
  street_view_url: string | null; satellite_url: string | null; property_id: string | null;
  roof_squares: number | null; roof_pitch_deg: number | null; has_geo?: boolean;
};

/**
 * Vista de la propiedad como en Supplements: foto de la casa (street view) + el techo
 * (satélite) + las MEDIDAS GRATIS de Google (roof squares + pitch), que se traen solas.
 * Reusable — se muestra desde Claim Review en adelante, en todos lados.
 */
export function PropertyRoofView({ claimId }: { claimId: string }) {
  const { t } = useI18n();
  const [s, setS] = useState<Summary | null>(null);
  const [measuring, setMeasuring] = useState(false);
  const [okStreet, setOkStreet] = useState(true);
  const [okSat, setOkSat] = useState(true);
  const tried = useRef(false);

  useEffect(() => {
    let alive = true;
    api.get<Summary>(`/api/claims/${claimId}/summary`).then(async (r) => {
      if (!alive) return;
      setS(r);
      // Medida gratis de Google: si aún no está, la traemos sola (una sola vez).
      if (r.roof_squares == null && !tried.current) {
        tried.current = true;
        setMeasuring(true);
        try { await api.post(`/api/claims/${claimId}/property-intel`, {}); const r2 = await api.get<Summary>(`/api/claims/${claimId}/summary`); if (alive) setS(r2); }
        catch { /* sin coords / sin key: queda sin medida */ } finally { if (alive) setMeasuring(false); }
      }
    }).catch(() => {});
    return () => { alive = false; };
  }, [claimId]);

  const street = s?.street_view_url || `/api/claims/${claimId}/streetview`;
  const sat = s?.satellite_url || `/api/claims/${claimId}/satellite`;
  const sq = s?.roof_squares;
  const pitchDeg = s?.roof_pitch_deg;
  const pitch = roofingPitch(pitchDeg); // ej. 24.8° → "6/12"
  const measureText = sq != null || pitch != null
    ? `${sq != null ? `${sq} sq` : ''}${sq != null && pitch != null ? ' · ' : ''}${pitch != null ? `${pitch} pitch` : ''}`
    : measuring ? t('prv.measuring') : t('prv.noMeasure');

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
        <div style={{ position: 'relative', height: 150, borderRadius: 10, overflow: 'hidden', background: 'var(--surface-2)' }}>
          {okStreet ? <img src={street} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={() => setOkStreet(false)} />
            : <div style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', fontSize: 'var(--fs-xl)', color: 'var(--muted)' }}><Home size={30} strokeWidth={1.75} /></div>}
          <span className="badge" style={{ position: 'absolute', top: 6, left: 6, fontSize: 'var(--fs-2xs)', background: 'rgba(0,0,0,.55)', color: '#fff' }}>{t('prv.house')}</span>
        </div>
        <div style={{ position: 'relative', height: 150, borderRadius: 10, overflow: 'hidden', background: 'var(--surface-2)' }}>
          {okSat ? <img src={sat} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={() => setOkSat(false)} />
            : <div style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', fontSize: 'var(--fs-xl)', color: 'var(--muted)' }}><Satellite size={30} strokeWidth={1.75} /></div>}
          <span className="badge" style={{ position: 'absolute', top: 6, left: 6, fontSize: 'var(--fs-2xs)', background: 'rgba(0,0,0,.55)', color: '#fff' }}>{t('prv.roof')}</span>
          <span className="badge" style={{ position: 'absolute', bottom: 6, left: 6, fontSize: 'var(--fs-2xs)', background: 'rgba(0,0,0,.6)', color: '#fff', fontWeight: 'var(--fw-bold)' }}><Ruler size={10} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {measureText}</span>
        </div>
      </div>
      {/* Medida de Google (roof squares + pitch) en tarjetas diseñadas (no texto plano). */}
      {(sq != null || pitch != null || measuring) && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <RoofStat icon={<Ruler size={15} strokeWidth={1.75} />} label={t('prv.roofArea')} value={sq != null ? `${sq}` : (measuring ? '…' : '—')} unit={sq != null ? 'sq' : undefined} />
          <RoofStat icon={<Ruler size={15} strokeWidth={1.75} />} label={t('prv.pitch')} value={pitch != null ? pitch : (measuring ? '…' : '—')} accent />
        </div>
      )}
    </div>
  );
}

/** Tarjeta de estadística del techo (área / pitch): diseño limpio y profesional. */
function RoofStat({ icon, label, value, unit, accent }: { icon: React.ReactNode; label: string; value: string; unit?: string; accent?: boolean }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderRadius: 12,
      border: '1px solid var(--line)', background: 'var(--card)', boxShadow: 'var(--shadow)', minWidth: 120,
    }}>
      <span style={{
        width: 30, height: 30, borderRadius: 9, display: 'grid', placeItems: 'center', fontSize: 'var(--fs-base)', flexShrink: 0,
        background: accent ? 'var(--accent-soft)' : 'var(--surface-2)', color: accent ? 'var(--accent)' : 'var(--ink)',
      }}>{icon}</span>
      <span style={{ display: 'grid', gap: 1 }}>
        <span style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--muted)' }}>{label}</span>
        <span style={{ fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-bold)', lineHeight: 1.1 }}>{value}{unit ? <span style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-semibold)', color: 'var(--muted)', marginLeft: 3 }}>{unit}</span> : null}</span>
      </span>
    </div>
  );
}
